import { Inject, Injectable, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Route,
  RouterModule,
  RouterStateSnapshot,
} from '@angular/router';
import {
  ListDetailStructureComponent,
  ListDetailStructureComponentModule,
} from '@icilalune/shell-ui';
import { DrupalUrlGuard } from '@madeinlune/ngrx-drupal-paths-to-routes';
import { EmptyComponent, EmptyComponentModule } from '@icilalune/utils';
import { RouteData } from '@icilalune/types';
import {
  blogDetailQuery,
  blogListQuery,
  clientListQuery,
  newsDetailQuery,
  newsListQuery,
  pageDetailQuery,
  pageForListQuery,
  projectDetailQuery,
  projectListQuery,
  solutionDetailQuery,
} from '@nx-icilalune/data/icilalune/queries';
import {
  SETTINGS_APP_INSTALLED,
  SETTINGS_APP_VERSION,
  SETTINGS_CAN_INSTALL,
  SETTINGS_MENU,
  SETTINGS_ROUTED_ALTERNATIVES_MENU,
} from '@nx-icilalune/icilalune/settings/tokens';
import {
  MAIN_MENU,
  ROUTED_ENTITY_ALTERNATIVES,
  SECONDARY_MENU,
} from '@nx-icilalune/data/icilalune/tokens';
import { filter, Observable, shareReplay } from 'rxjs';
import { MainMenuTreeItem } from '@nx-icilalune/data/icilalune/types';
import { Tree } from '@nx-icilalune/drupal-types';
import { PwaService } from '@nx-icilalune/icilalune/pwa';
import { isEqual } from '@madeinlune/ngx-operators';
import { ShellComponent } from './shell/shell.component';

@Injectable({ providedIn: 'root' })
export class MainMenuResolver implements Resolve<any> {
  constructor(
    @Inject(MAIN_MENU)
    private mainMenu$: Observable<Tree<MainMenuTreeItem>>
  ) {}

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> {
    return this.mainMenu$.pipe(
      filter((mainMenu) => mainMenu?.items?.length > 0)
    );
  }
}

export interface AppRoute extends Route {
  data?: RouteData;
  children?: AppRoute[];
}

export type AppRoutes = AppRoute[];

const routes: AppRoutes = [
  {
    path: '',
    loadComponent: async () =>
      (await import('@icilalune/shell-ui')).MainLayoutComponent,
    children: [
      {
        path: '2d/:language',
        children: [
          {
            path: '',
            pathMatch: 'full',
            data: {
              isHome: true,
            },
            loadChildren: async () =>
              (await import('@icilalune/elevator-feature'))
                .ElevatorComponentModule,
          },
          {
            path: 'scully/:type/:id',
            loadComponent: async () =>
              (await import('@nx-icilalune/scully')).ScullyComponent,
          },
          {
            path: 'clients',
            data: {
              context: 'list:clients',
              queries: {
                list: clientListQuery,
              },
            },
            loadChildren: async () =>
              (await import('@icilalune/list-feature/client'))
                .ClientsComponentModule,
          },
          {
            path: 'settings',
            data: {
              context: 'detail:settings',
              queries: {
                main: pageDetailQuery,
              },
            },
            providers: [
              {
                provide: SETTINGS_MENU,
                deps: [SECONDARY_MENU],
                useFactory: (
                  secondaryMenu$: Observable<Tree<MainMenuTreeItem>>
                ) => {
                  return secondaryMenu$.pipe(isEqual(), shareReplay(1));
                },
              },
              {
                provide: SETTINGS_APP_VERSION,
                useValue: '0.0.0',
              },
              {
                provide: SETTINGS_APP_INSTALLED,
                deps: [PwaService],
                useFactory: (pwaService: PwaService) => {
                  return pwaService.installed$;
                },
              },
              {
                provide: SETTINGS_CAN_INSTALL,
                deps: [PwaService],
                useFactory: (pwaService: PwaService) => {
                  return pwaService.canInstall$;
                },
              },
              {
                provide: SETTINGS_ROUTED_ALTERNATIVES_MENU,
                deps: [ROUTED_ENTITY_ALTERNATIVES],
                useFactory: (alternatives$) => alternatives$,
              },
            ],
            loadChildren: async () =>
              (await import('@nx-icilalune/icilalune/settings'))
                .SettingsComponentModule,
          },
          {
            path: '',
            component: ListDetailStructureComponent,
            children: [
              /*{
                path: 'projects',
                data: { animation: 'slide', context: 'list:projects' },
                loadChildren: async () =>
                  (await import('@icilalune/list-feature'))
                    .ProjectsComponentModule,
              },*/
              {
                path: 'projects/:pageId',
                data: {
                  context: 'list:projects',
                  queries: {
                    main: pageForListQuery,
                    list: projectListQuery,
                  },
                },
                loadComponent: async () =>
                  (await import('@icilalune/list-feature/project'))
                    .ProjectListComponent,
              },
              {
                path: 'projects/project/:projectId',
                loadChildren: async () =>
                  (await import('@icilalune/detail-feature/project'))
                    .ProjectComponentModule,
                data: {
                  context: 'detail:project',
                  queries: {
                    main: projectDetailQuery,
                  },
                },
              },
              {
                path: 'all-news/:pageId',
                data: {
                  context: 'list:news',
                  queries: {
                    main: pageForListQuery,
                    list: newsListQuery,
                  },
                },
                loadComponent: async () =>
                  (await import('@icilalune/list-feature/news')).NewsComponent,
              },
              {
                path: 'news/:newsId',
                data: {
                  context: 'detail:news',
                  queries: {
                    main: newsDetailQuery,
                    list: newsListQuery,
                  },
                },
                loadChildren: async () =>
                  (await import('@icilalune/detail-feature/news'))
                    .NewsComponentModule,
              },
              {
                path: 'all-blogs',
                data: {
                  context: 'list:blog',
                  queries: {
                    main: pageForListQuery,
                    list: blogListQuery,
                  },
                },
                loadChildren: async () =>
                  (await import('@icilalune/list-feature/blog'))
                    .BlogsComponentModule,
              },
              {
                path: 'blogs/:blogId',
                data: {
                  context: 'detail:blog',
                  queries: {
                    main: blogDetailQuery,
                    list: blogListQuery,
                  },
                },
                loadChildren: async () =>
                  (await import('@icilalune/detail-feature/blog'))
                    .BlogComponentModule,
              },
              {
                path: 'services',
                data: {
                  context: 'detail:services',
                  queries: {
                    main: pageDetailQuery,
                  },
                },
                loadChildren: async () =>
                  (await import('@icilalune/detail-feature/services'))
                    .ServicesComponentModule,
              },
              {
                path: 'made-in-lune',
                data: { animation: 'slide', context: 'detail:madeinlune' },
                loadChildren: async () =>
                  (await import('@icilalune/list-feature/made-in-lune'))
                    .MadeInLuneComponentModule,
              },
              {
                path: 'solutions',
                data: {
                  context: 'detail:solution',
                  queries: {
                    main: solutionDetailQuery,
                  },
                },
                loadChildren: async () =>
                  (await import('@icilalune/detail-feature/solution'))
                    .SolutionComponentModule,
              },
              {
                path: 'odyssee/:pageId',
                data: {
                  context: 'detail:odyssee',
                  queries: {
                    main: pageDetailQuery,
                  },
                },
                loadChildren: async () =>
                  (await import('@icilalune/detail-feature/odyssee'))
                    .OdysseePageComponentModule,
              },
            ],
          },
        ],
      },
      {
        path: '**',
        component: EmptyComponent,
        canActivate: [DrupalUrlGuard],
      },
    ],
  },
]; // sets up routes constant where you define your routes

// configures NgModule imports and exports
@NgModule({
  imports: [
    RouterModule.forChild(routes),
    EmptyComponentModule,
    ListDetailStructureComponentModule,
  ],
  exports: [RouterModule],
  providers: [],
  declarations: [ShellComponent],
})
export class IcilaluneShellRoutingModule {}
